import { AfterViewInit, Component, ElementRef, inject, OnDestroy, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import KeenSlider, { KeenSliderInstance } from 'keen-slider';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { AuthenticationService } from '@auth';
import { AlertType } from '@core/enums';
import { Logger } from '@core/services';

const log = new Logger('ForgotPasswordComponent');

@UntilDestroy()
@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['../login/login.component.scss'],
  standalone: false,
})
export class ForgotPasswordComponent implements AfterViewInit, OnDestroy {
  isLoading = false;
  selectedLanguage = 'de';
  @ViewChild('fpSliderRef') fpSliderRef: ElementRef<HTMLElement>;
  @ViewChild('fpImageSliderRef') imageSliderRef: ElementRef<HTMLElement>;
  currentSlide = 0;
  slider: KeenSliderInstance = null;
  imageSlider: KeenSliderInstance = null;
  imageSources: string[] = ['assets/images/login/slide-image-1.jpg', 'assets/images/login/slide-image-2.jpg', 'assets/images/login/slide-image-3.jpg', 'assets/images/login/slide-image-4.jpg'];
  alertMessage: string = null;
  form: FormGroup = new FormGroup({
    username: new FormControl('', [Validators.required, Validators.minLength(3)]),
  });
  protected readonly AlertType = AlertType;
  private readonly _authenticationService = inject(AuthenticationService);

  get alertType(): AlertType {
    if (this.alertMessage === 'PASSWORD_RESET_SUCCESS_MESSAGE') {
      return AlertType.SUCCESS;
    }
    return AlertType.ERROR;
  }

  get isSuccess(): boolean {
    return this.alertType === AlertType.SUCCESS;
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this._initImageSlider();
      this._initTextSlider();
    }, 500);
  }

  ngOnDestroy() {
    if (this.slider) this.slider.destroy();
  }

  submit() {
    if (this.form.invalid) {
      return;
    }
    this.form.disable();
    this.isLoading = true;

    this._authenticationService
      .submitPasswordReset(this.form.value.username)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.alertMessage = 'PASSWORD_RESET_SUCCESS_MESSAGE';
          this.form.enable();
          this.form.reset();
          this.isLoading = false;
        },
        error: (error) => {
          if (typeof error.error.message !== 'string') {
            this.alertMessage = 'Something went wrong';
          } else {
            this.alertMessage = error.error.message;
          }
          log.error('Error sending password reset email', error);
          this.form.enable();
          this.isLoading = false;
        },
      });
  }

  private _initImageSlider() {
    this.imageSlider = new KeenSlider(
      this.imageSliderRef.nativeElement,
      {
        loop: true,
      },
      [
        (slider) => {
          let timeout: any;
          let mouseOver = false;

          function clearNextTimeout() {
            clearTimeout(timeout);
          }

          function nextTimeout() {
            clearTimeout(timeout);
            if (mouseOver) return;
            timeout = setTimeout(() => {
              slider.next();
            }, 5000);
          }

          slider.on('created', () => {
            slider.container.addEventListener('mouseover', () => {
              mouseOver = true;
              clearNextTimeout();
            });
            slider.container.addEventListener('mouseout', () => {
              mouseOver = false;
              nextTimeout();
            });
            nextTimeout();
          });
          slider.on('dragStarted', clearNextTimeout);
          slider.on('animationEnded', nextTimeout);
          slider.on('updated', nextTimeout);
        },
      ],
    );
  }

  private _initTextSlider() {
    this.slider = new KeenSlider(this.fpSliderRef.nativeElement, {
      initial: this.currentSlide,
      slideChanged: (s) => {
        this.currentSlide = s.track.details.rel;
      },
    });
  }
}
